import type { useTenant } from "@nestoca/multi-tenant";
import type { AssignedAdvisor } from "@shared/constants";
import type { TFunction } from "i18next";

export type SupportContactId = "name" | "teamName" | "phone" | "email";

export type SupportInfoType = {
  id: SupportContactId;
  type?: string;
  data: string;
  canCopy?: boolean;
  link?: string;
};

type Tenant = ReturnType<typeof useTenant>;

type LocalizableTenantKey =
  | "applicationSupportTeamName"
  | "applicationSupportPhoneNumber";

const EXTERNAL_BROKER_UNDERWRITER = {
  name: "Tony Le",
  phone: "514-526-2442",
  email: "tony.le@nesto.ca",
};

const phoneItem = (type: string, phone?: string | null): SupportInfoType => ({
  id: "phone",
  type,
  data: phone || "",
  canCopy: true,
  link: `tel:${phone}`,
});

const emailItem = (type: string, email?: string | null): SupportInfoType => ({
  id: "email",
  type,
  data: email || "",
  canCopy: true,
  link: `mailto:${email}`,
});

const withoutEmptyRows = (items: SupportInfoType[]) =>
  items.filter((item) => item.data.trim());

export const getAdvisorContact = (
  advisor: AssignedAdvisor,
  t: TFunction
): SupportInfoType[] =>
  withoutEmptyRows([
    {
      id: "name",
      type: t("advisorName", { ns: "applications" }),
      data: [advisor.firstName, advisor.lastName].filter(Boolean).join(" "),
    },
    emailItem(t("email"), advisor.email),
    phoneItem(t("phone"), advisor.phone),
  ]);

export const getExternalBrokerContact = (t: TFunction): SupportInfoType[] =>
  withoutEmptyRows([
    {
      id: "name",
      type: t("underwriter"),
      data: EXTERNAL_BROKER_UNDERWRITER.name,
    },
    emailItem(t("email", { ns: "docs" }), EXTERNAL_BROKER_UNDERWRITER.email),
    phoneItem(t("phone", { ns: "docs" }), EXTERNAL_BROKER_UNDERWRITER.phone),
  ]);

export const getGenericContact = ({
  tenant,
  isFrench,
  isBusinessTime,
  t,
}: {
  tenant: Tenant;
  isFrench: boolean;
  isBusinessTime: boolean;
  t: TFunction;
}): SupportInfoType[] => {
  const getLocalizedTenantValue = (key: LocalizableTenantKey) =>
    (isFrench && tenant?.[`${key}FR`]) || tenant?.[key];

  // tenants without the setting keep the phone limited to business hours
  const shouldShowSupportPhone =
    isBusinessTime ||
    !!tenant?.settings?.cmaFormSettings?.settings?.businessHours
      ?.showSupportPhoneOutside;

  return withoutEmptyRows([
    {
      id: "teamName",
      data: getLocalizedTenantValue("applicationSupportTeamName") || "",
    },
    emailItem(t("email"), tenant?.applicationSupportEmail),
    phoneItem(
      t("phone"),
      shouldShowSupportPhone
        ? getLocalizedTenantValue("applicationSupportPhoneNumber")
        : null
    ),
  ]);
};
